import React from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../Appstore/store';
import { AddSmsCampaignsList } from '../Features/smscampaignlist/smscampaignlist';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { useDropzone } from 'react-dropzone';

interface FileUploadProps {
    field: any;
    form: any;
    label: string;
}

interface SmsCampaignListAdd {
    name: string;
    numbers: File[];
    message: string;
    id?: number;
    retries: number;
    schedule_time: string;
    retry_minutes:string|number
}

const Addsmscampaignlistretry: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { id }: any = useParams();

    const { error, smscampaignlistdata, loading } = useSelector((state: RootState) => state.smscampaignlist);
    const isActive = (path: string) => location.pathname === path ? 'active' : '';

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required').trim(),
        numbers: Yup.array()
            .min(1, 'At least one file is required')
            .test('fileType', 'Only .xlsx and .xls files are allowed', (files) => {
                return files?.every(file =>
                    file.name.endsWith('.xlsx') || file.name.endsWith('.xls')
                );
            }),
        message: Yup.string().required('Message is required').trim(),
        scheduletime: Yup.string().required('Schedule time is required'),
        retries: Yup.number().required('Retries is required').min(0, 'Retries must be at least 0'),
    });

    const initialValues = {
        name: '',
        numbers: [],
        message: '',
        scheduletime: '',
        retries: '',
        retry_minutes:''
    };

    const navigatetobackpage = () => {
        navigate(-1);
    };

    const formatDateTimeTo12Hour = (datetimeValue: string) => {
        const [date, time] = datetimeValue.split('T');
        let [hours, minutes] = time.split(':');

        const isPM = Number(hours) >= 12;
        const ampm = isPM ? 'PM' : 'AM';
        hours = String(Number(hours) % 12 || 12).padStart(2, '0');

        return `${date} ${hours}:${minutes}:00 ${ampm}`;
    };



    const FileUpload: React.FC<FileUploadProps> = ({ field, form, label }) => {
        const { setFieldValue } = form;

        const onDrop = (acceptedFiles: File[]) => {
            setFieldValue(field.name, acceptedFiles);
        };

        const handleRemoveFile = (index: number) => {
            const newFiles = field.value.filter((_: File, i: number) => i !== index);
            setFieldValue(field.name, newFiles);
        };

        const { getRootProps, getInputProps } = useDropzone({
            onDrop,
            accept: {
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                'application/vnd.ms-excel': ['.xls']
            },
            multiple: false
        });

        return (
            <>
                <label htmlFor="name">Numbers (Upload Excel File)
                    <span className="text-danger">*</span>
                </label>
                <div {...getRootProps()} className="dropzone">
                    <input {...getInputProps()} />
                    <p className={`form-control shadow-none mb-0 ${form.touched.numbers && form.errors.numbers ? 'is-invalid' : ''}`}>Drag 'n' drop some files here, or click to select files</p>
                </div>
                {field.value && field.value.length > 0 && (
                    <div className='d-flex flex-wrap'>
                        {field.value.map((file: File, index: number) => (
                            <div key={index} className='d-flex align-items-center mb-3 border p-2 mt-2'>
                                <p className='mb-0'>{file.name}</p>
                                <span className='ms-2' onClick={() => handleRemoveFile(index)}>
                                    <i className="fa-solid fa-square-xmark text-danger" style={{ cursor: 'pointer' }}></i>
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {form.touched.numbers && form.errors.numbers && (
                    <div className="text-danger">{form.errors.numbers}</div>
                )}
            </>

        );
    };

    return (
        <>
            <div className="wrapper">
                <div className="content-wrapper">
                    <section className="content">
                        <div className="container-fluid">
                            <div className="card mt-4">
                                <div className="card-header bg-white p-3 d-flex justify-content-md-between">
                                    <h2 className="w-100 mb-md-0 fs-3">Add SMS Campaign List</h2>
                                    <Link to={''} className='text-decoration-none'>
                                        <button onClick={navigatetobackpage} className='btn btn-info text-white d-flex align-items-center fw-semibold'>
                                            <IoMdArrowRoundBack /> Back
                                        </button>
                                    </Link>
                                </div>
                                <div className="card-body">
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={async (values, { setSubmitting }) => {
                                            const SmsCampaignListData: SmsCampaignListAdd = {
                                                name: values.name,
                                                numbers: values.numbers,
                                                message: values.message,
                                                schedule_time: formatDateTimeTo12Hour(values.scheduletime),
                                                retries: Number(values.retries),
                                                retry_minutes: Number(values.retry_minutes)
                                            };

                                            try {
                                                const response: any = await dispatch(AddSmsCampaignsList({ sms_campaign_id: id ?? 0, addData: SmsCampaignListData }));
                                                if (response.payload && response.payload.status === 'success') {
                                                    navigate(`/admin/sms-campaigns/view/sms-campaigns-list/${id}/list`);
                                                }
                                            } finally {
                                                setSubmitting(false);
                                            }
                                        }}
                                    >
                                        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                                            <Form>
                                                <div className="row">
                                                    <div className="form-group mb-3 col-md-6">
                                                        <label htmlFor="name">Name
                                                            <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control shadow-none ${touched.name && errors.name ? 'is-invalid' : ''}`}
                                                            id="name"
                                                            name="name"
                                                            placeholder="Enter a Name"
                                                            value={values.name}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.name && errors.name && <div className="text-danger">{errors.name}</div>}
                                                    </div>
                                                    <div className="form-group mb-3 col-md-6">
                                                        <label htmlFor='message'>Message<span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            className={`form-control shadow-none ${touched.message && errors.message ? 'is-invalid' : ''}`}
                                                            id="message"
                                                            name="message"
                                                            placeholder="Enter a Message"
                                                            value={values.message}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.message && errors.message && <div className="text-danger">{errors.message}</div>}
                                                    </div>
                                                </div>

                                                <div className="row">
                                                    <div className="form-group mb-3 col-md-6">
                                                        <label htmlFor="scheduletime">
                                                            Schedule Time<span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="datetime-local"
                                                            className={`form-control shadow-none ${touched.scheduletime && errors.scheduletime ? 'is-invalid' : ''}`}
                                                            id="scheduletime"
                                                            name="scheduletime"
                                                            value={values.scheduletime}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.scheduletime && errors.scheduletime && (
                                                            <div className="text-danger">{errors.scheduletime}</div>
                                                        )}
                                                    </div>


                                                    <div className="form-group mb-3 col-md-6">
                                                        <label htmlFor="retries">Retries<span className="text-danger">*</span></label>
                                                        <input
                                                            type="number"
                                                            className={`form-control shadow-none ${touched.retries && errors.retries ? 'is-invalid' : ''}`}
                                                            id="retries"
                                                            name="retries"
                                                            placeholder="Enter a Retries Number"
                                                            value={values.retries}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.retries && errors.retries && (
                                                            <div className="text-danger">{errors.retries}</div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className='row'>
                                                    <div className="form-group mb-3 col-md-6">
                                                        <Field name="numbers" component={FileUpload} />
                                                        {/* {touched.numbers && errors.numbers && (
                                                            <div className="text-danger">{errors.numbers}</div>
                                                        )} */}
                                                    </div>

                                                </div>

                                                <div className="d-flex">
                                                    <button type="submit" className="btn btn-primary me-2" disabled={isSubmitting}>
                                                        {isSubmitting ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                <span className='ps-2'>Submitting...</span>
                                                            </>
                                                        ) : (
                                                            "Submit"
                                                        )}
                                                    </button>
                                                    <Link to={`/admin/sms-campaigns/view/sms-campaigns-list/${id}/list`} className={`nav-link ${isActive('admin/sms-campaigns/view/sms-campaigns-list/:id')}`}>
                                                        <button type="button" className="btn btn-secondary">Cancel</button>
                                                    </Link>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </section>
                </div >
            </div >
        </>
    );
};

export default Addsmscampaignlistretry;
